// ============================================================================
// FCM SERVICE HYBRIDE - Web + Android + iOS + TV
// Fichier : src/services/fcmService.js
// ============================================================================

import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import app from '../firebase/config'
import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging'
import { getFirestore, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'

class FCMService {
    constructor() {
        this.messaging   = null
        this.token       = null
        this.vapidKey    = null
        this.isNative    = Capacitor.isNativePlatform()
        this.isSupported = false
        this.callbacks   = {
            onTokenReceived: [],
            onMessage:       [],
            onError:         []
        }

        this.init()
    }

    // ========================================================================
    // INITIALISATION
    // ========================================================================

    async init() {
        try {
            console.log(`🚀 FCM Service - Initialisation (${this.isNative ? 'NATIVE' : 'WEB'})...`)

            if (this.isNative) {
                await this.initNative()
            } else {
                await this.initWeb()
            }

        } catch (error) {
            console.error('❌ Erreur initialisation FCM:', error)
            this.triggerCallback('onError', error)
        }
    }

    // ========================================================================
    // INIT WEB (Navigateur)
    // ========================================================================

    async initWeb() {
        if (!('serviceWorker' in navigator) || !('Notification' in window)) {
            console.warn('⚠️ FCM non supporté par ce navigateur')
            return
        }

        this.isSupported = true
        this.messaging = getMessaging(app)

        console.log('✅ FCM Web initialisé')

        await this.registerServiceWorker()
        this.setupMessageListener()
    }

    // ========================================================================
    // INIT NATIVE (Android/iOS)
    // ========================================================================

    async initNative() {
        console.log('📱 Initialisation FCM Native (Capacitor)')

        // Vérifier les permissions
        const permStatus = await PushNotifications.checkPermissions()
        console.log('📋 Permissions actuelles:', permStatus.receive)

        // Écouter les événements
        PushNotifications.addListener('registration', (token) => {
            console.log('✅ Token FCM Native reçu:', token.value.substring(0, 40) + '...')
            this.token = token.value
            this.triggerCallback('onTokenReceived', token.value)
            this.saveToken(token.value)
        })

        PushNotifications.addListener('registrationError', (error) => {
            console.error('❌ Erreur enregistrement FCM Native:', error)
            this.triggerCallback('onError', error)
        })

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('📨 Notification reçue (app au premier plan):', notification)
            this.triggerCallback('onMessage', {
                notification: {
                    title: notification.title,
                    body: notification.body
                },
                data: notification.data
            })
        })

        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            console.log('🔔 Notification cliquée:', notification)
        })

        this.isSupported = true
        console.log('✅ FCM Native initialisé')
    }

    // ========================================================================
    // ENREGISTREMENT SERVICE WORKER (WEB uniquement)
    // ========================================================================

    async registerServiceWorker() {
        try {
            const registration = await navigator.serviceWorker.register(
                '/firebase-messaging-sw.js',
                { scope: '/' }
            )

            console.log('✅ Service Worker enregistré:', registration.scope)

            if (registration.installing) {
                await new Promise((resolve) => {
                    registration.installing.addEventListener('statechange', (e) => {
                        if (e.target.state === 'activated') {
                            console.log('✅ Service Worker activé')
                            resolve()
                        }
                    })
                })
            }

            return registration

        } catch (error) {
            console.error('❌ Erreur Service Worker:', error.message)
        }
    }

    // ========================================================================
    // DEMANDE DE PERMISSION ET TOKEN
    // ========================================================================

    async requestPermissionAndGetToken(vapidKey) {
        if (!this.isSupported) {
            console.warn('⚠️ FCM non supporté')
            return null
        }

        if (this.isNative) {
            return await this.requestPermissionNative()
        } else {
            return await this.requestPermissionWeb(vapidKey)
        }
    }

    // ========================================================================
    // PERMISSION WEB
    // ========================================================================

    async requestPermissionWeb(vapidKey) {
        try {
            console.log('📋 Demande de permission notifications (WEB)...')

            const permission = await Notification.requestPermission()

            if (permission !== 'granted') {
                console.warn('⚠️ Permission refusée par l\'utilisateur')
                return null
            }

            console.log('✅ Permission accordée')
            this.vapidKey = vapidKey

            const swRegistration = await navigator.serviceWorker.ready
            console.log('✅ Service Worker prêt')

            console.log('🔑 Récupération du token FCM Web...')

            const token = await getToken(this.messaging, {
                vapidKey:                    vapidKey,
                serviceWorkerRegistration:   swRegistration
            })

            if (token) {
                console.log('✅ Token FCM Web obtenu:', token.substring(0, 40) + '...')
                this.token = token
                this.triggerCallback('onTokenReceived', token)
                await this.saveToken(token)
                return token
            } else {
                console.warn('⚠️ Token vide')
                return null
            }

        } catch (error) {
            console.error('❌ Erreur token FCM Web:', error.code, error.message)
            this.triggerCallback('onError', error)
            return null
        }
    }

    // ========================================================================
    // PERMISSION NATIVE
    // ========================================================================

    async requestPermissionNative() {
        try {
            console.log('📋 Demande de permission notifications (NATIVE)...')

            let permStatus = await PushNotifications.checkPermissions()

            if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions()
            }

            if (permStatus.receive !== 'granted') {
                console.warn('⚠️ Permission refusée')
                return null
            }

            console.log('✅ Permission accordée')

            // Enregistrer l'appareil pour recevoir des notifications
            await PushNotifications.register()
            console.log('📱 Appareil enregistré pour FCM Native')

            // Le token sera reçu via l'événement 'registration'
            // On retourne une promesse qui se résoudra quand on aura le token
            return new Promise((resolve) => {
                const timeout = setTimeout(() => {
                    console.warn('⚠️ Timeout en attendant le token')
                    resolve(null)
                }, 10000) // 10 secondes max

                const checkToken = setInterval(() => {
                    if (this.token) {
                        clearInterval(checkToken)
                        clearTimeout(timeout)
                        resolve(this.token)
                    }
                }, 100)
            })

        } catch (error) {
            console.error('❌ Erreur permission Native:', error)
            return null
        }
    }

    // ========================================================================
    // SAUVEGARDE TOKEN DANS FIRESTORE
    // ========================================================================

    async saveToken(token) {
        try {
            console.log('💾 Sauvegarde du token...')

            localStorage.setItem('fcm_token', token)
            localStorage.setItem('fcm_token_date', new Date().toISOString())

            const auth   = getAuth()
            const userId = auth.currentUser?.uid || 'anonymous'

            console.log('👤 Utilisateur:', userId)
            console.log('📱 Plateforme:', this.getPlatform())

            const db = getFirestore()

            await setDoc(doc(db, 'fcmTokens', token), {
                token:      token,
                userId:     userId,
                platform:   this.getPlatform(),
                app:        'radio', // différencie cette app des autres apps Johnny
                deviceType: this.isNative ? 'native' : 'web',
                deviceInfo: this.getDeviceInfo(),
                createdAt:  serverTimestamp(),
                updatedAt:  serverTimestamp()
            }, { merge: true })

            console.log('✅ Token sauvegardé dans Firestore (fcmTokens)')
            return true

        } catch (error) {
            console.error('❌ Erreur sauvegarde token:', error.message)
            return false
        }
    }

    // ========================================================================
    // ÉCOUTE MESSAGES (WEB uniquement)
    // ========================================================================

    setupMessageListener() {
        if (!this.messaging) return

        onMessage(this.messaging, (payload) => {
            console.log('📨 Message FCM reçu (Web, premier plan):', payload)
            this.triggerCallback('onMessage', payload)

            if (Notification.permission === 'granted') {
                this.showNotification(payload)
            }
        })
    }

    // ========================================================================
    // AFFICHAGE NOTIFICATION (WEB)
    // ========================================================================

    async showNotification(payload) {
        try {
            const title   = payload.notification?.title || 'Radio 100% Johnny'
            const options = {
                body:               payload.notification?.body || '',
                icon:               payload.notification?.icon || '/images/logo.png',
                badge:              '/images/logo.png',
                tag:                payload.data?.tag || 'notification',
                data:               payload.data || {},
                requireInteraction: payload.data?.requireInteraction === 'true'
            }

            if (this.isNative) {
                // Sur native, les notifications sont gérées automatiquement
                return
            }

            const registration = await navigator.serviceWorker.ready
            await registration.showNotification(title, options)

        } catch (error) {
            console.error('❌ Erreur affichage notification:', error)
        }
    }

    // ========================================================================
    // SUPPRESSION TOKEN
    // ========================================================================

    async deleteToken() {
        try {
            console.log('🗑️ Suppression du token FCM...')

            if (this.isNative) {
                await PushNotifications.removeAllListeners()
            } else if (this.messaging && this.token) {
                await deleteToken(this.messaging)
            }

            localStorage.removeItem('fcm_token')
            localStorage.removeItem('fcm_token_date')

            const db = getFirestore()
            if (this.token) {
                await deleteDoc(doc(db, 'fcmTokens', this.token))
            }

            this.token = null
            console.log('✅ Token FCM supprimé')
            return true

        } catch (error) {
            console.error('❌ Erreur suppression token:', error)
            return false
        }
    }

    // ========================================================================
    // CALLBACKS
    // ========================================================================

    on(event, callback) {
        if (this.callbacks[event]) this.callbacks[event].push(callback)
    }

    off(event, callback) {
        if (this.callbacks[event]) {
            this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback)
        }
    }

    triggerCallback(event, data) {
        this.callbacks[event]?.forEach(cb => {
            try { cb(data) } catch (e) { console.error(`Callback ${event} error:`, e) }
        })
    }

    // ========================================================================
    // UTILITAIRES
    // ========================================================================

    getPlatform() {
        if (this.isNative) {
            return Capacitor.getPlatform() // 'android', 'ios', etc.
        }
        
        const ua = navigator.userAgent.toLowerCase()
        if (/android/.test(ua))          return 'android'
        if (/iphone|ipad|ipod/.test(ua)) return 'ios'
        if (/mobile/.test(ua))           return 'mobile'
        return 'web'
    }

    getDeviceInfo() {
        return {
            isNative:         this.isNative,
            platform:         this.getPlatform(),
            capacitorVersion: this.isNative ? Capacitor.getPlatform() : 'N/A',
            userAgent:        navigator.userAgent,
            language:         navigator.language,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            timezone:         Intl.DateTimeFormat().resolvedOptions().timeZone
        }
    }

    getToken()      { return this.token }
    isInitialized() { return this.isSupported }
    hasToken()      { return this.token !== null }

    getStatus() {
        return {
            supported:   this.isSupported,
            initialized: this.isInitialized(),
            hasToken:    this.hasToken(),
            isNative:    this.isNative,
            platform:    this.getPlatform(),
            permission:  this.isNative ? 'N/A' : Notification.permission,
            token:       this.token ? this.token.substring(0, 40) + '...' : null
        }
    }
}

// ============================================================================
// EXPORT
// ============================================================================

const fcmService = new FCMService()
export default fcmService